import { useFormContext } from 'react-hook-form';

import useApiRequest from '~/hooks/api/useApiRequest';
import api from '~/services/api';
import { IDatesValidation } from '~/services/api/clientAllocation';
import useRole from '~/store/user/hooks/useRole';
import { normalizeDateString } from '~/utils/date/date';

import { IAddAllocationFormValues } from '../../AddClientAllocation/types';

interface UseClientAllocationRangeProps {
  clinicId: string;
  actTeamId: string;
}

const initialDatesValidation: IDatesValidation = {
  isValid: true,
  users: [],
};

const useClientAllocationRange = ({ clinicId, actTeamId }: UseClientAllocationRangeProps) => {
  const { watch } = useFormContext<IAddAllocationFormValues>();

  const [dateRange, includeWeekends, teamMembersAllocations] = watch([
    'dateRange',
    'includeWeekends',
    'teamMembersAllocations',
  ]);

  const { isActTeamMember, isGlobalUser } = useRole();

  const from = dateRange[0];
  const to = dateRange[1];

  const selectedMemberIds = teamMembersAllocations
    .filter(m => m.clientsAllocations.length > 0 || m.locationGroupAllocations.length > 0)
    .map(m => m.id);

  const enabled = !!(from && to && selectedMemberIds.length > 0);

  const { data, refetch: validateClientAllocationRange } = useApiRequest<IDatesValidation>(
    async () => {
      const basePayload = {
        from: normalizeDateString(from),
        to: normalizeDateString(to, true),
        includeWeekends,
      };

      if (isGlobalUser) {
        return api.clientAllocation
          .validateDateRangeGU({ clinicId }, basePayload)
          .then(r => r.data);
      }

      if (isActTeamMember) {
        return api.clientAllocation
          .validateOwnDateRange({ clinicId, teamId: actTeamId }, basePayload)
          .then(r => r.data);
      }

      return api.clientAllocation
        .validateDateRange(
          { clinicId, teamId: actTeamId },
          { ...basePayload, userIds: selectedMemberIds },
        )
        .then(r => r.data);
    },
    {
      enabled,
      dependencies: [from, to, includeWeekends, selectedMemberIds.length],
    },
  );

  return {
    datesValidation: enabled ? data ?? initialDatesValidation : initialDatesValidation,
    validateClientAllocationRange,
  };
};

export default useClientAllocationRange;
